import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ROUTES } from '@/app/config/constants';
import { useAppSelector } from '@/app/store/hooks';
import {
  ChangePasswordDialog,
  selectPasswordChangeRequired,
  useAuth,
  useSignOutAfterPasswordChange,
} from '@/features/authentication';
import { capitalize, getInitial } from '@/shared/utils/stringUtils';

interface HeaderUserMenuProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}

const MENU_ITEM_SELECTOR = '[role="menuitem"]';

export function HeaderUserMenu({ isOpen, onToggle, onClose }: HeaderUserMenuProps) {
  const { user, logout, isMegaAdmin } = useAuth();
  const { pathname } = useLocation();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [changingPassword, setChangingPassword] = useState(false);
  const passwordChangeRequired = useAppSelector(selectPasswordChangeRequired);
  const signOutAfterPasswordChange = useSignOutAfterPasswordChange();

  const closePasswordDialog = useCallback(() => {
    setChangingPassword(false);
    /* The menu item that opened it is gone with the menu. */
    triggerRef.current?.focus();
  }, []);

  // Close on click-outside or Escape, returning focus to the trigger.
  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: MouseEvent): void => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleKeyDown = (event: globalThis.KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      onClose();
      triggerRef.current?.focus();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Close on navigation. Section 23.6 writes this as `[closeUserMenu]`, which
  // only fires on mount; the dependency has to be the pathname.
  useEffect(() => {
    onClose();
  }, [pathname, onClose]);

  // Move focus into the menu when it opens.
  useEffect(() => {
    if (!isOpen) return;
    menuRef.current?.querySelector<HTMLElement>(MENU_ITEM_SELECTOR)?.focus();
  }, [isOpen]);

  if (!user) return null;

  /*
    The role slug, capitalised -- except for a Mega Admin, whose slug is the same `admin` a tenant Admin
    carries, so the chip read "Admin". It shows the role's own name from `GET /users/me` (`role.name`,
    "Mega Admin") instead. Every other role is shown exactly as before.
  */
  const role = isMegaAdmin && user.roleName ? user.roleName : user.roles?.[0];
  /*
    NB-631: beneath the name, a Mega Admin's chip shows their own user name (`userName` from GET /users/me, the user
    table's user name column) rather than repeating the role. A blank user name keeps the role line as before.
  */
  const megaAdminUserName = isMegaAdmin ? user.userName.trim() : '';

  const handleMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const nodes = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>(MENU_ITEM_SELECTOR) ?? [],
    );
    if (nodes.length === 0) return;

    const current = nodes.indexOf(document.activeElement as HTMLElement);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        nodes[(current + 1) % nodes.length]?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        nodes[(current - 1 + nodes.length) % nodes.length]?.focus();
        break;
      case 'Home':
        event.preventDefault();
        nodes[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        nodes[nodes.length - 1]?.focus();
        break;
      case 'Tab':
        // Tabbing out of a menu closes it -- the correct menu pattern, and
        // less hostile than trapping focus.
        onClose();
        break;
      default:
        break;
    }
  };

  const handleLogout = (): void => {
    onClose();
    logout();
  };

  return (
    <div className="header__user-menu" ref={wrapperRef}>
      <button
        type="button"
        ref={triggerRef}
        className="header__user-trigger"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls="header-user-menu"
        onClick={onToggle}
      >
        <span className="header__avatar">
          {user.avatar ? <img src={user.avatar} alt="" /> : getInitial(user.name)}
        </span>
        <span className="header__user-meta">
          <span className="header__user-name">{user.name}</span>
          {megaAdminUserName ? (
            <span className="header__user-role">{megaAdminUserName}</span>
          ) : role ? (
            <span className="header__user-role">{isMegaAdmin ? role : capitalize(role)}</span>
          ) : null}
        </span>
        <i
          className={`pi pi-chevron-down header__user-chevron${
            isOpen ? ' header__user-chevron--open' : ''
          }`}
          aria-hidden="true"
        />
      </button>

      {isOpen ? (
        <div
          className="header__dropdown"
          id="header-user-menu"
          role="menu"
          ref={menuRef}
          onKeyDown={handleMenuKeyDown}
        >
          <div className="header__dropdown-header">
            <p className="header__dropdown-name">{user.name}</p>
            <p className="header__dropdown-email">{user.email}</p>
          </div>

          <NavLink role="menuitem" className="header__dropdown-item" to={ROUTES.PROFILE}>
            <i className="pi pi-user header__dropdown-item-icon" aria-hidden="true" />
            <span>Profile</span>
          </NavLink>
          <NavLink role="menuitem" className="header__dropdown-item" to={ROUTES.SETTINGS}>
            <i className="pi pi-cog header__dropdown-item-icon" aria-hidden="true" />
            <span>Settings</span>
          </NavLink>
          {/* Opens the dialog over the current page; there is no Change Password page to go to. */}
          <button
            type="button"
            role="menuitem"
            className="header__dropdown-item"
            onClick={() => {
              onClose();
              setChangingPassword(true);
            }}
          >
            <i className="pi pi-lock header__dropdown-item-icon" aria-hidden="true" />
            <span>Change Password</span>
          </button>

          <div className="header__dropdown-divider" />

          <button
            type="button"
            role="menuitem"
            className="header__dropdown-item header__dropdown-item--action"
            onClick={handleLogout}
          >
            <i className="pi pi-sign-out header__dropdown-item-icon" aria-hidden="true" />
            <span>Logout</span>
          </button>
        </div>
      ) : null}

      {/*
        Outside the dropdown, so closing the menu does not close it. A user still on their first password has
        the shell's mandatory dialog instead, never this one.
      */}
      {changingPassword && !passwordChangeRequired ? (
        <ChangePasswordDialog
          onClose={closePasswordDialog}
          /*
            NB-552: the platform ends every session of the account once the password changes -- this one included
            -- so a change from here, like the first-sign-in change, goes on to the sign-in page with "Your password
            has been changed. Please sign in with your new password." It used to stay here with a toast, on a
            session that is now dead.
          */
          onChanged={() => {
            setChangingPassword(false);
            signOutAfterPasswordChange();
            return undefined;
          }}
        />
      ) : null}
    </div>
  );
}
